import { GenericInvoiceInput } from "../types/genericInvoice";

export interface RawPhoneBillingService {
  serviceType: string;
  units: number;
  totalAmount: number;
  unitPrice?: number;
  averageUnitPrice?: number;
  description: string;
}

export interface RawPhoneBillingRecord {
  uid: string;
  phoneNumber: string;
  billingPeriod: string;
  services: RawPhoneBillingService[];
  grandTotal: number;
  closedAt: string;
}

type QuantityKind = "minute" | "message" | "unit";

const SERVICE_COPY: Record<
  string,
  {
    title: string;
    quantityKind: QuantityKind;
  }
> = {
  voice_inbound: { title: "Inbound calls", quantityKind: "minute" },
  voice_outbound: { title: "Outbound calls", quantityKind: "minute" },
  sms_inbound: { title: "Inbound messages", quantityKind: "message" },
  sms_outbound: { title: "Outbound messages", quantityKind: "message" },
  voicemail: { title: "Voicemail", quantityKind: "minute" },
  call_recording: { title: "Call recording", quantityKind: "minute" },
  voice: { title: "Calls", quantityKind: "minute" },
  sms: { title: "Messages", quantityKind: "message" },
};

function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatQuantity(units: number, kind: QuantityKind): string {
  if (kind === "minute") return pluralize(units, "minute", "minutes");
  if (kind === "message") return pluralize(units, "message", "messages");
  return pluralize(units, "unit", "units");
}

function getServiceCopy(serviceType: string) {
  if (SERVICE_COPY[serviceType]) {
    return SERVICE_COPY[serviceType];
  }

  return {
    title: serviceType
      ? serviceType.charAt(0).toUpperCase() + serviceType.slice(1).replace(/_/g, " ")
      : "Service usage",
    quantityKind: "unit" as QuantityKind,
  };
}

function buildUserFacingDescription(params: {
  units: number;
  unitPrice: number;
  quantityKind: QuantityKind;
}): string {
  const { units, unitPrice, quantityKind } = params;
  const quantity = formatQuantity(units, quantityKind);

  if (unitPrice > 0) {
    const rateLabel =
      quantityKind === "minute" ? "per minute" : quantityKind === "message" ? "per message" : "each";

    return `${quantity} billed at $${unitPrice.toFixed(4)} ${rateLabel}.`;
  }

  return `${quantity} included in this billing period.`;
}

/**
 * Adapter: period-summary snapshot → GenericInvoiceInput for invoice creation.
 */
export const parsePhoneBillingToGenericInvoice = (data: RawPhoneBillingRecord): GenericInvoiceInput => {
  const billableServices = (data.services || []).filter(
    (service) => Number(service.units) > 0 && Number(service.totalAmount) > 0,
  );

  const lineItems = billableServices.map((service) => {
    const unitPrice = Number(service.unitPrice ?? service.averageUnitPrice ?? 0);
    const units = Number(service.units) || 0;
    const { title, quantityKind } = getServiceCopy(service.serviceType);
    const quantityLabel = formatQuantity(units, quantityKind);

    return {
      id: `${service.serviceType || "service"}-${data.uid}`,
      title: `${title} · ${quantityLabel}`,
      amount: Number(service.totalAmount) || 0,
      description: buildUserFacingDescription({
        units,
        unitPrice,
        quantityKind,
      }),
    };
  });

  return {
    identifierId: `INV-USAGE-${data.phoneNumber.replace(/\D/g, "")}-${data.billingPeriod}`,
    client: {
      name: `Phone Service Customer`,
      alias: data.phoneNumber,
      email: "",
      phone: data.phoneNumber,
    },
    lineItems,
    notes: `Usage charges for ${data.billingPeriod}.`,
    sendEmail: true,
    externalUid: data.uid,
  };
};
